/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { dateValidator } from '@ftr/forms'
import { DestroySubscribers } from '@ftr/foundation'
import moment from 'moment-timezone'
import { Observable, distinctUntilChanged, filter, skip, takeUntil, tap } from 'rxjs'
import { ComplexFilterData } from '../ComplexFilter'
import { DateRadioListOptions } from '../DateRadioListFilter'
import { DateRangeFilter } from '../DateRangeFilter'
import { ComplexFilterService } from '../complex-filter.service'
import { COMPLEX_FILTER_DATE_FORMAT } from '../date/ComplexDateFilterUtils'

type RangeType = 'range'
type DateFromString = string | undefined
type DateToString = string | undefined

type DateRangeFilterValue = [RangeType, DateFromString, DateToString]

@Component({
  selector: 'ftr-complex-filter-date-range',
  templateUrl: './complex-filter-date-range.component.html',
  styleUrls: ['./complex-filter-date-range.component.css'],
})
export class ComplexFilterDateRangeComponent extends DestroySubscribers implements OnInit {
  /**
   * Filter configuration, must be specific to the radio-list filter type
   */
  @Input() filter: DateRangeFilter
  @Input() visibilityToggled: Observable<boolean>
  @Output() filterChanged = new EventEmitter<ComplexFilterData>()

  readonly dateFormat = COMPLEX_FILTER_DATE_FORMAT

  internalFormGroup = new UntypedFormGroup(
    {
      dateRangeFrom: new UntypedFormControl(undefined, [Validators.required, dateValidator]),
      dateRangeTo: new UntypedFormControl(undefined, [Validators.required, dateValidator]),
    },
    { validators: this.dateRangeValidator() },
  )

  private lastUpdatedControlName = ''
  private lastDateRangeFrom: DateFromString
  private lastDateRangeTo: DateToString

  constructor(private readonly complexFilterService: ComplexFilterService) {
    super()
  }

  ngOnInit(): void {
    this.buildInternalFormGroup()
    this.listenToFilterChange()
    this.listenToVisibilityChange()
  }

  listenToFilterChange(): void {
    this.complexFilterService
      .listenToFilterChange([this.filter])
      .pipe(takeUntil(this.finalize))
      .subscribe(filters => {
        const [_, dateRangeFrom, dateRangeTo] = (
          filters[this.filter.filterKey]?.length !== 0
            ? filters[this.filter.filterKey]
            : [DateRadioListOptions.SelectDateRange, undefined, undefined]
        ) as DateRangeFilterValue

        this.lastDateRangeFrom = dateRangeFrom
        this.lastDateRangeTo = dateRangeTo
        this.updateControls(dateRangeFrom, dateRangeTo)
      })
  }

  asFormControl(control: AbstractControl): UntypedFormControl {
    return control as UntypedFormControl
  }

  private dateRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const controlDateRangeFrom = group.get('dateRangeFrom')
      const controlDateRangeTo = group.get('dateRangeTo')

      if (!controlDateRangeFrom || !controlDateRangeTo) {
        return null
      }

      if (controlDateRangeFrom.errors?.invalidDateRangeFrom) {
        controlDateRangeFrom.setErrors(null)
        controlDateRangeFrom.updateValueAndValidity({ emitEvent: false })
      }
      if (controlDateRangeTo.errors?.invalidDateRangeTo) {
        controlDateRangeTo.setErrors(null)
        controlDateRangeTo.updateValueAndValidity({ emitEvent: false })
      }

      const momentDateRangeFrom = moment(controlDateRangeFrom.value, COMPLEX_FILTER_DATE_FORMAT)
      const momentDateRangeTo = moment(controlDateRangeTo.value, COMPLEX_FILTER_DATE_FORMAT)

      if (
        momentDateRangeFrom.isValid() &&
        momentDateRangeTo.isValid() &&
        momentDateRangeFrom.isAfter(momentDateRangeTo)
      ) {
        if (this.lastUpdatedControlName === 'dateRangeFrom') {
          controlDateRangeFrom.setErrors({
            ...controlDateRangeFrom.errors,
            invalidDateRangeFrom: 'From date cannot be after To date',
          })
        } else if (this.lastUpdatedControlName === 'dateRangeTo') {
          controlDateRangeTo.setErrors({
            ...controlDateRangeTo.errors,
            invalidDateRangeTo: 'To date cannot be before From date',
          })
        }
      }

      return null
    }
  }

  /**
   * This is to ensure the value of the filter resets to the previous one
   * when we change it manually and close the modal without applying.
   * Still works when applied immediately, because lastDateRangeFrom & lastDateRangeTo is updated.
   */
  private listenToVisibilityChange(): void {
    this.visibilityToggled
      .pipe(
        takeUntil(this.finalize),
        tap(visible => {
          if (!visible) {
            this.updateControls(this.lastDateRangeFrom, this.lastDateRangeTo)
          }
        }),
      )
      .subscribe()
  }

  private buildInternalFormGroup(): void {
    this.internalFormGroup.controls.dateRangeFrom.valueChanges
      .pipe(
        takeUntil(this.finalize),
        distinctUntilChanged(momentDateComparator),
        // Date picker component calls blur when opened, we skip to avoid extra control updates
        skip(1),
      )
      .subscribe(() => {
        this.lastUpdatedControlName = 'dateRangeFrom'
        this.lastDateRangeFrom = this.internalFormGroup.controls.dateRangeFrom.value
        this.internalFormGroup.updateValueAndValidity()
      })

    this.internalFormGroup.controls.dateRangeTo.valueChanges
      .pipe(
        takeUntil(this.finalize),
        distinctUntilChanged(momentDateComparator),
        // Date picker component calls blur when opened, we skip to avoid extra control updates
        skip(1),
      )
      .subscribe(() => {
        this.lastUpdatedControlName = 'dateRangeTo'
        this.lastDateRangeTo = this.internalFormGroup.controls.dateRangeTo.value
        this.internalFormGroup.updateValueAndValidity()
      })

    this.internalFormGroup.valueChanges
      .pipe(
        takeUntil(this.finalize),
        distinctUntilChanged(
          (previous, current) =>
            momentDateComparator(previous.dateRangeFrom, current.dateRangeFrom) &&
            momentDateComparator(previous.dateRangeTo, current.dateRangeTo),
        ),
        filter(() => this.internalFormGroup.valid),
      )
      .subscribe(value => {
        this.filterChanged.emit({
          [this.filter.filterKey]: [
            DateRadioListOptions.SelectDateRange,
            this.getFormattedDate(value.dateRangeFrom),
            this.getFormattedDate(value.dateRangeTo),
          ],
        })
      })
  }

  private updateControls(dateRangeFrom: DateFromString, dateRangeTo: DateToString): void {
    this.updateControlDateValueIfValid(this.internalFormGroup.controls.dateRangeFrom, dateRangeFrom)
    this.updateControlDateValueIfValid(this.internalFormGroup.controls.dateRangeTo, dateRangeTo)
  }

  private updateControlDateValueIfValid(control: AbstractControl, value: string | undefined): void {
    const momentValue = moment(value, COMPLEX_FILTER_DATE_FORMAT)
    if (momentValue.isValid()) {
      control.setValue(momentValue, { emitEvent: false })
    } else {
      control.reset()
    }
  }

  private getFormattedDate(value: string | moment.Moment): string {
    return (moment.isMoment(value) ? value : moment(value)).format(this.dateFormat)
  }
}

function momentDateComparator(one: moment.Moment, two: moment.Moment): boolean {
  return moment.isMoment(one) && moment.isMoment(two) && one.isSame(two)
}
